import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import type { FlagUpdate, Folder, MessageDetail } from '@/api/types';
import {
  adjustUnreadCount,
  patchMessageDetailFlags,
  patchMessageDetailLabels,
  patchMessageFlags,
  patchMessageLabels,
  patchMessageFolder,
  removeMessageFromList,
  setFolderCounts,
  type FolderCountDeltas,
  type FolderCountTotals,
  type LabelKeywordEdit,
  type MessagePageCache,
} from './messagePatches';
import {
  patchConversationFlags,
  patchConversationLabels,
  removeMessagesFromConversations,
  type ConversationPageCache,
} from './conversationPatches';
import {
  patchConversationFlags as patchConversationDetailFlags,
  patchConversationLabels as patchConversationDetailLabels,
} from '@/features/conversation/cache';
import { conversationKeys } from '@/features/conversation/queryKeys';

/**
 * Declarative cache patches, and the (React-free) applier that runs them.
 *
 * A patch says *what changed in the world*, not *which caches to poke*. That
 * indirection is the whole point: an optimistic mutation and an inbound event
 * from the live stream (ADR 0002) describe the same change, so they emit the
 * same patch and the cache ends up in the same state either way
 * (docs/plan/06-frontend-v1.md §1; the transport moved from WebSocket to SSE
 * and this seam did not have to, which is the evidence it was the right one).
 *
 * The applier takes a `QueryClient` and nothing else — no hooks, no context —
 * so it is callable from a socket handler, a test, or a mutation callback.
 */
export type CachePatch =
  /** Flags changed on messages that are already in the cache. */
  | {
      kind: 'message-flags';
      accountId: string;
      messageIds: string[];
      update: FlagUpdate;
      /**
       * The live stream's reading of a frame: the whole flag set each message
       * has NOW, written as add/remove, rather than a change someone made.
       * Every read message in such a frame arrives as "add seen", including
       * ones that were read all along — exact for a message row, but a
       * conversation row only knows how many members are unread, not which,
       * and would count them read a second time. So a stream patch leaves the
       * conversation lists to their `sync` (`conversationSignals.ts`).
       */
      absolute?: true;
      /**
       * With `absolute`: the label keywords among the frame's raw flags — the
       * whole set each of these messages has now (plan/17 §6).
       */
      labelKeywords?: string[];
    }
  /**
   * Label keywords changed on messages (plan/17 §5): the picker, a chip's ×.
   * `messageIds` absent means every message of the account — a rename or a
   * delete that has finished rewriting (`edit.rename` / `edit.remove`).
   */
  | { kind: 'message-labels'; accountId: string; messageIds?: string[]; edit: LabelKeywordEdit }
  /** Messages left their folder for another one (move, archive). */
  | { kind: 'messages-moved'; accountId: string; messageIds: string[]; targetFolderId: string }
  /** Messages are gone from every list of this account (delete/expunge). */
  | { kind: 'messages-removed'; accountId: string; messageIds: string[] }
  /** Signed adjustments to folder-tree counters. */
  | { kind: 'folder-counts'; accountId: string; deltas: FolderCountDeltas }
  /**
   * The server's own counters for one folder, absolute.
   *
   * Emitted only by the live-update reducer, which is handed totals rather
   * than deltas (`folder_counts`, `new_mail`, `expunge` all carry the counts
   * after the change). It shares `folders()` with the delta patch above, so a
   * locally-made change and a pushed one still converge on one cache entry.
   */
  | { kind: 'folder-counts-set'; accountId: string; folderId: string; totals: FolderCountTotals };

/** A verbatim copy of the cache entries a patch set touches, for rollback. */
export type CacheSnapshot = ReadonlyArray<readonly [QueryKey, unknown]>;

/**
 * The query-key prefixes a patch can write to.
 *
 * Used three times per mutation: to cancel in-flight fetches before patching
 * (so a slow response cannot overwrite the optimistic state), to snapshot for
 * rollback, and to mark stale on settle.
 */
export function affectedQueryKeys(patch: CachePatch): QueryKey[] {
  const keys = queryKeys.account(patch.accountId);
  switch (patch.kind) {
    case 'message-flags':
    case 'message-labels':
    case 'messages-moved':
    case 'messages-removed':
      // The merged list of ADR 0056 holds rows of this account too, so it is
      // cancelled, snapshotted and invalidated with them. It is listed
      // unconditionally: whether it is mounted is the query cache's business,
      // and a patch that skipped it while it happened to be cold would leave a
      // stale row behind the next time the unified view was opened.
      //
      // The conversation lists (ADR 0077) likewise: a verb on a conversation is
      // a verb on its messages, and the row it came from must move with them —
      // and come back with them on Undo or a rollback.
      return [
        keys.messagesScope(),
        keys.messageScope(),
        queryKeys.unifiedMessagesScope(),
        keys.conversationsScope(),
        queryKeys.unifiedConversationsScope(),
        // And the reading pane's conversation stacks, so its star and its
        // unread marks move with the list and roll back with it.
        conversationKeys.scope(patch.accountId),
      ];
    case 'folder-counts':
    case 'folder-counts-set':
      return [keys.folders()];
  }
}

function uniqueKeys(patches: readonly CachePatch[]): QueryKey[] {
  const seen = new Map<string, QueryKey>();
  for (const patch of patches) {
    for (const key of affectedQueryKeys(patch)) seen.set(JSON.stringify(key), key);
  }
  return [...seen.values()];
}

/**
 * `setQueriesData`, minus its two side effects on entries a patch did not
 * change.
 *
 * `setQueryData` stamps an entry as freshly fetched — `dataUpdatedAt = now`,
 * `isInvalidated = false` — even when the updater hands back the very same
 * object. So an `expunge` frame for the Inbox, whose "remove these rows" runs
 * over every cached list, used to mark Junk's hidden (and stale, because the
 * Report spam that filled it had just invalidated it) list FRESH for the 30 s
 * list stale time: opening Junk showed the old empty copy and fetched nothing.
 *
 * Here an entry the updater leaves untouched is not written at all, and one
 * that is written keeps its staleness: a patch edits a cached copy, it does
 * not make that copy the server's truth.
 */
function patchQueries<T>(
  client: QueryClient,
  queryKey: QueryKey,
  update: (current: T | undefined) => T | undefined,
): void {
  for (const query of client.getQueryCache().findAll({ queryKey })) {
    const current = query.state.data as T | undefined;
    const next = update(current);
    if (next === current || next === undefined) continue;
    const wasInvalidated = query.state.isInvalidated;
    client.setQueryData<T>(query.queryKey, next);
    if (wasInvalidated) {
      void client.invalidateQueries({
        queryKey: query.queryKey,
        exact: true,
        refetchType: 'none',
      });
    }
  }
}

/**
 * Messages left their folder: out of every conversation row that held them.
 * A conversation id is minted per folder and a message id per account, so the
 * per-account lists match on the id alone and the merged one on the account.
 */
function removeFromConversationLists(
  client: QueryClient,
  accountId: string,
  messageIds: readonly string[],
): void {
  patchQueries<ConversationPageCache>(client, queryKeys.account(accountId).conversationsScope(), (cache) =>
    removeMessagesFromConversations(cache, messageIds),
  );
  patchQueries<ConversationPageCache>(client, queryKeys.unifiedConversationsScope(), (cache) =>
    removeMessagesFromConversations(cache, messageIds, accountId),
  );
}

/** A label edit, into every list, detail and conversation that holds the messages. */
function applyLabelEdit(
  client: QueryClient,
  accountId: string,
  messageIds: readonly string[] | undefined,
  edit: LabelKeywordEdit,
): void {
  const keys = queryKeys.account(accountId);
  patchQueries<MessagePageCache>(client, keys.messagesScope(), (cache) =>
    patchMessageLabels(cache, messageIds, edit),
  );
  patchQueries<MessagePageCache>(client, queryKeys.unifiedMessagesScope(), (cache) =>
    patchMessageLabels(cache, messageIds, edit, accountId),
  );
  patchQueries<MessageDetail>(client, keys.messageScope(), (detail) =>
    patchMessageDetailLabels(detail, messageIds, edit),
  );
  patchQueries<ConversationPageCache>(client, keys.conversationsScope(), (cache) =>
    patchConversationLabels(cache, messageIds, edit),
  );
  patchQueries<ConversationPageCache>(client, queryKeys.unifiedConversationsScope(), (cache) =>
    patchConversationLabels(cache, messageIds, edit, accountId),
  );
  patchConversationDetailLabels(client, accountId, messageIds, edit);
}

/** Applies one patch to every matching cache entry. */
export function applyCachePatch(client: QueryClient, patch: CachePatch): void {
  const keys = queryKeys.account(patch.accountId);

  switch (patch.kind) {
    case 'message-flags':
      patchQueries<MessagePageCache>(client, keys.messagesScope(), (cache) =>
        patchMessageFlags(cache, patch.messageIds, patch.update),
      );
      // Account-matched: see `ownedBy` in messagePatches.ts — ids are unique
      // only within an account, and this list holds several.
      patchQueries<MessagePageCache>(client, queryKeys.unifiedMessagesScope(), (cache) =>
        patchMessageFlags(cache, patch.messageIds, patch.update, patch.accountId),
      );
      patchQueries<MessageDetail>(client, keys.messageScope(), (detail) =>
        patchMessageDetailFlags(detail, patch.messageIds, patch.update),
      );
      // Only a change someone made here (an optimistic mutation, or one
      // broadcast from another window): it names exactly the members that
      // change. A stream frame's absolute sets do not (see `absolute`).
      if (!patch.absolute) {
        patchQueries<ConversationPageCache>(client, keys.conversationsScope(), (cache) =>
          patchConversationFlags(cache, patch.messageIds, patch.update),
        );
        patchQueries<ConversationPageCache>(client, queryKeys.unifiedConversationsScope(), (cache) =>
          patchConversationFlags(cache, patch.messageIds, patch.update, patch.accountId),
        );
      }
      // The restore it returns is not needed: every caller that can roll back
      // snapshotted this scope first (see `affectedQueryKeys`).
      patchConversationDetailFlags(client, patch.accountId, patch.messageIds, patch.update);
      // The frame's label keywords, absolute per message: rows and details,
      // never the conversation lists' unions (they sync, as for flags).
      if (patch.labelKeywords !== undefined) {
        applyLabelEdit(client, patch.accountId, patch.messageIds, { set: patch.labelKeywords });
      }
      return;

    case 'message-labels':
      applyLabelEdit(client, patch.accountId, patch.messageIds, patch.edit);
      return;

    case 'messages-moved':
      // The rows leave whichever list currently holds them; the open detail
      // follows the message to its new folder instead of 404-ing on refetch.
      patchQueries<MessagePageCache>(client, keys.messagesScope(), (cache) =>
        removeMessageFromList(cache, patch.messageIds),
      );
      patchQueries<MessagePageCache>(client, queryKeys.unifiedMessagesScope(), (cache) =>
        removeMessageFromList(cache, patch.messageIds, patch.accountId),
      );
      patchQueries<MessageDetail>(client, keys.messageScope(), (detail) =>
        patchMessageFolder(detail, patch.messageIds, patch.targetFolderId),
      );
      removeFromConversationLists(client, patch.accountId, patch.messageIds);
      return;

    case 'messages-removed':
      patchQueries<MessagePageCache>(client, keys.messagesScope(), (cache) =>
        removeMessageFromList(cache, patch.messageIds),
      );
      patchQueries<MessagePageCache>(client, queryKeys.unifiedMessagesScope(), (cache) =>
        removeMessageFromList(cache, patch.messageIds, patch.accountId),
      );
      removeFromConversationLists(client, patch.accountId, patch.messageIds);
      for (const messageId of patch.messageIds) {
        // Prefix, not `exact`: one message can hold more than one detail entry
        // (the remote-content modes of ADR 0018), and a deleted message must
        // leave none of them behind.
        client.removeQueries({ queryKey: keys.message(messageId) });
        client.removeQueries({ queryKey: keys.messageRaw(messageId), exact: true });
      }
      return;

    case 'folder-counts':
      client.setQueryData<Folder[]>(keys.folders(), (folders) =>
        adjustUnreadCount(folders, patch.deltas),
      );
      return;

    case 'folder-counts-set':
      client.setQueryData<Folder[]>(keys.folders(), (folders) =>
        setFolderCounts(folders, patch.folderId, patch.totals),
      );
      return;
  }
}

/** Applies a patch set in order. */
export function applyCachePatches(client: QueryClient, patches: readonly CachePatch[]): void {
  for (const patch of patches) applyCachePatch(client, patch);
}

/**
 * Cancels in-flight fetches for everything the patches will touch.
 *
 * Without this, a list request that started before the optimistic patch can
 * land after it and resurrect the row the user just archived.
 */
export async function cancelAffectedQueries(
  client: QueryClient,
  patches: readonly CachePatch[],
): Promise<void> {
  await Promise.all(
    uniqueKeys(patches).map((queryKey) => client.cancelQueries({ queryKey })),
  );
}

/** Copies every cache entry the patches can touch, for verbatim restore. */
export function snapshotAffectedQueries(
  client: QueryClient,
  patches: readonly CachePatch[],
): CacheSnapshot {
  return uniqueKeys(patches).flatMap((queryKey) => client.getQueriesData({ queryKey }));
}

/** Restores a snapshot verbatim. This is the rollback half of every mutation. */
export function restoreSnapshot(client: QueryClient, snapshot: CacheSnapshot): void {
  for (const [queryKey, data] of snapshot) client.setQueryData(queryKey, data);
}

/**
 * Marks the affected queries stale **without refetching**.
 *
 * `refetchType: 'none'` is the deliberate choice from §1: the next mount or
 * user interaction picks up the truth, and a user mid-triage never has the list
 * yanked out from under them by a refetch they did not ask for.
 */
export async function invalidateAffectedQueries(
  client: QueryClient,
  patches: readonly CachePatch[],
): Promise<void> {
  await Promise.all(
    uniqueKeys(patches).map((queryKey) =>
      client.invalidateQueries({ queryKey, refetchType: 'none' }),
    ),
  );
}
